import './StudentsAndInvites.scss';
import { useGetStudents } from 'queryHooks/Organization/Students/useGetStudents';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationStore } from 'stores/Organization/OrganizationStore';
import { useStudentsStore } from 'stores/Organization/StudentsStore';
import { Loader, Tab, Tabs } from '@trackman/web-shared-components';
import { useGetInvites } from 'queryHooks/Organization/Invites/useGetInvites';
import { filterOrganizationInvitesByClass, filterOrganizationStudentsByClass } from 'utilities/organizationDataFilter';
import ImportStudentListButton from '../Invites/ImportStudentListButton/ImportStudentListButton';
import InviteStudentsButton from '../Invites/InviteStudentsButton/InviteStudentsButton';
import { NoPendingInvitations } from '../Students/NoPendingInvitations/NoPendingInvitations';
import { StudentList } from '../Students/StudentList/StudentList';
import { NoStudentsFound } from '../Students/NoStudentsFound/NoStudentsFound';
import { PendingInviteList } from '../Invites/PendingInvitesList/PendingInvitesList';
import { handleSearchStudent } from 'utilities/search-helper';

export const StudentsAndInvites = ({filter, setFilter}: {filter: string, setFilter: (value: string) => void}) => {
  const selectedOrganizationId = useOrganizationStore((state) => state.selectedOrganizationId);
  const selectedClassId = useStudentsStore((state) => state.selectedClassId);

  const { data: studentList, isPending: isStudentListPending } = useGetStudents(selectedOrganizationId);
  const { data: inviteList, isPending: isInviteListPending } = useGetInvites(selectedOrganizationId);

  return (
    <>
      {!isStudentListPending && !isInviteListPending ? (
        <StudentsAndInvitesList studentList={studentList} inviteList={inviteList} selectedClassId={selectedClassId} filter={filter} setFilter={setFilter}  />
      ) : (
        <div className='new-loader-wrapper'>
          <Loader size='large' />
        </div>
      )}
    </>
  );
};

const StudentsAndInvitesList = ({ studentList, inviteList, selectedClassId, filter, setFilter }) => {
  const { t } = useTranslation();

  const getStudentsForClass = filterOrganizationStudentsByClass(studentList, selectedClassId);
  const getInvitesForClass = filterOrganizationInvitesByClass(inviteList, selectedClassId);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const areTopButtonsVisible = selectedTabIndex === 0 ? getStudentsForClass.length > 0 : getInvitesForClass.length > 0;

  const getNumberOfFilteredStudents =
    selectedTabIndex === 0 ? handleSearchStudent(filter, getStudentsForClass).length : getStudentsForClass.length;
  const getNumberOfFilteredInvites =
    selectedTabIndex === 1 ? handleSearchStudent(filter, getInvitesForClass).length : getInvitesForClass.length;

  const handleTabChange = (index: number) => {
    setFilter('');
    setSelectedTabIndex(index);
  };

  return (
    <div className='students-and-invites-wrapper'>
      <div className='students-and-invites-header'>
        <Tabs value={selectedTabIndex} onChange={(event, newValue: number) => handleTabChange(newValue)}>
          <Tab label={t('SeeStudents', { ns: 'studentsPage', number: getNumberOfFilteredStudents })} />
          <Tab label={t('SeePendingInvites', { ns: 'studentsPage', number: getNumberOfFilteredInvites })} />
        </Tabs>
        {areTopButtonsVisible ? (
          <div key={selectedTabIndex} className='buttons-wrapper'>
            <ImportStudentListButton intent='primary' />
            <InviteStudentsButton intent='primary' />
          </div>
        ) : null}
      </div>

      <div role='tabpanel' hidden={selectedTabIndex !== 0} id='class-students-tab' aria-labelledby='class-students-tab'>
        {!getStudentsForClass.length ? <NoStudentsFound /> : <StudentList studentList={getStudentsForClass} filter={filter} />}
      </div>
      <div role='tabpanel' hidden={selectedTabIndex !== 1} id='pending-invites-tab' aria-labelledby='pending-invites-tab'>
        {!getInvitesForClass.length ? <NoPendingInvitations /> : <PendingInviteList inviteList={getInvitesForClass} filter={filter} />}
      </div>
    </div>
  );
};
